// Step 2: Main Attachment Handler Class
import { requestHeaderWithAccessToken, serverUrl, getStoredAuth, APIOneDriveUrl, requestHeaderForOneDrive } from "../auth/auth.service";
import { AttachmentProcessResult, OneDriveFilesList, PamsApiResponse, PamsAttachment, PamsFileData } from "./attachement.model";
class OutlookAttachmentHandler {
  private pamsBaseUrl: string;
  private authToken: string;

  constructor(pamsBaseUrl: string, authToken: string) {
    this.pamsBaseUrl = pamsBaseUrl;
    this.authToken = authToken;
  }

  // Step 3: Get all attachments from current email
  async getAllAttachments(): Promise<any[]> {
    //Office.AttachmentDetails
    return new Promise((resolve, reject) => {
      try {
        const item = Office.context.mailbox.item;

        if (!item) {
          reject(new Error("No email item available"));
          return;
        }

        // For read mode - attachments are directly available
        if (item.attachments && item.attachments.length > 0) {
          //console.log(`Found ${item.attachments.length} attachments in read mode`);
          resolve(item.attachments);
          return;
        }

        // For compose mode - use getAttachmentsAsync (requires Mailbox 1.8+)
        if (typeof item.getAttachmentsAsync === "function") {
          item.getAttachmentsAsync((result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              //console.log(`Found ${result.value.length} attachments in compose mode`);
              resolve(result.value);
            } else {
              reject(new Error(`Failed to get attachments: ${result.error?.message}`));
            }
          });
        } else {
          // Fallback: No attachments or method not available
          //console.log("No attachments found or getAttachmentsAsync not available");
          resolve([]);
        }
      } catch (error) {
        reject(error);
      }
    });
  }

  // Step 4: Get attachment content
  async getAttachmentContent(attachmentId: string): Promise<{ content: string; format: any }> {
    //Office.MailboxEnums.AttachmentContentFormat
    return new Promise((resolve, reject) => {
      try {
        const item = Office.context.mailbox.item;

        if (!item || typeof item.getAttachmentContentAsync !== "function") {
          reject(new Error("getAttachmentContentAsync not available"));
          return;
        }

        item.getAttachmentContentAsync(attachmentId, (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve({ content: result.value.content, format: result.value.format });
          } else {
            reject(new Error(`Failed to get attachment content: ${result.error?.message}`));
          }
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  // Step 5: Generate hash for duplicate detection
  async generateAttachmentHash(content: string, name: string, size: number): Promise<string> {
    try {
      // Create a simple hash using attachment metadata
      const hashInput = `${name}_${size}_${content.substring(0, 100)}`;

      // Use Web Crypto API if available, otherwise fallback to simple hash
      if (crypto && crypto.subtle) {
        const encoder = new TextEncoder();
        const data = encoder.encode(hashInput);
        const hashBuffer = await crypto.subtle.digest("SHA-256", data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map((byte) => byte.toString(16).padStart(2, "0")).join("");
      } else {
        // Simple fallback hash function
        let hash = 0;
        for (let i = 0; i < hashInput.length; i++) {
          const char = hashInput.charCodeAt(i);
          hash = (hash << 5) - hash + char;
          hash = hash & hash; // Convert to 32bit integer
        }
        return Math.abs(hash).toString(16);
      }
    } catch (error) {
      console.error("Error generating hash:", error);
      // Fallback to timestamp-based hash
      return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
  }

  // Step 6: Check if attachment already exists in Pams
  async checkAttachmentExists(hash: string): Promise<{ exists: boolean; existingId?: string }> {
    try {
      const response = await fetch(`${this.pamsBaseUrl}/api/attachments/check-duplicate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.authToken}`,
        },
        body: JSON.stringify({ hash }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: PamsApiResponse = await response.json();

      if (result.success) {
        return {
          exists: result.data?.exists || false,
          existingId: result.data?.id,
        };
      } else {
        throw new Error(result.error || "Failed to check for duplicates");
      }
    } catch (error) {
      console.error("Error checking for duplicates:", error);
      // In case of error, assume it doesn't exist to avoid blocking upload
      return { exists: false };
    }
  }

  // Step 7: Upload attachment to Pams
  async uploadAttachmentToPams(attachment: PamsAttachment): Promise<AttachmentProcessResult> {
    try {
      const response = await fetch(`${this.pamsBaseUrl}/api/attachments/upload`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.authToken}`,
        },
        body: JSON.stringify(attachment),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: PamsApiResponse = await response.json();

      if (result.success) {
        return {
          success: true,
          message: "Attachment uploaded successfully",
          attachmentId: result.data?.id,
        };
      } else {
        return {
          success: false,
          message: result.error || "Upload failed",
          error: result.error,
        };
      }
    } catch (error) {
      console.error("Error uploading attachment:", error);
      return {
        success: false,
        message: "Network error during upload",
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  // Step 8: Process single attachment
  async processSingleAttachment(
    attachmentDetails: Office.AttachmentDetails
  ): Promise<AttachmentProcessResult> {
    try {
      //console.log(`Processing attachment: ${attachmentDetails.name}`);

      // Get attachment content
      const contentResult = await this.getAttachmentContent(attachmentDetails.id);

      // Generate hash for duplicate detection
      const hash = await this.generateAttachmentHash(
        contentResult.content,
        attachmentDetails.name,
        attachmentDetails.size
      );

      // Check if attachment already exists
      const duplicateCheck = await this.checkAttachmentExists(hash);

      if (duplicateCheck.exists) {
        //console.log(`Attachment "${attachmentDetails.name}" already exists in Pams`);
        return {
          success: true,
          message: `Attachment "${attachmentDetails.name}" already exists in Pams`,
          isDuplicate: true,
          attachmentId: duplicateCheck.existingId,
        };
      }

      // Get email context
      const emailContext = this.getEmailContext();

      // Prepare attachment for upload
      const pamsAttachment: PamsAttachment = {
        name: attachmentDetails.name,
        size: attachmentDetails.size,
        contentType: this.getContentType(attachmentDetails.name, contentResult.format),
        content: contentResult.content,
        hash: hash,
        emailId: emailContext.itemId,
        emailSubject: emailContext.subject,
        uploadDate: new Date().toISOString(),
        source: emailContext.mode,
      };

      // Upload to Pams
      const uploadResult = await this.uploadAttachmentToPams(pamsAttachment);

      if (uploadResult.success) {
        //console.log(`✅ Successfully uploaded "${attachmentDetails.name}" to Pams`);
      } else {
        console.error(`❌ Failed to upload "${attachmentDetails.name}":`, uploadResult.error);
      }

      return uploadResult;
    } catch (error) {
      console.error(`Error processing attachment "${attachmentDetails.name}":`, error);
      return {
        success: false,
        message: `Failed to process "${attachmentDetails.name}"`,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  // Step 9: Process all attachments from current email
  async processAllAttachments(): Promise<{total: number; successful: number; failed: number; duplicates: number; results: AttachmentProcessResult[];}> {
    try {
      //console.log("Starting to process all attachments...");

      // Get all attachments
      const attachments = await this.getAllAttachments();

      if (attachments.length === 0) {
        //console.log("No attachments found in this email");
        return {
          total: 0,
          successful: 0,
          failed: 0,
          duplicates: 0,
          results: [],
        };
      }

      //console.log(`Found ${attachments.length} attachment(s) to process`);

      // Process each attachment
      const results: AttachmentProcessResult[] = [];
      let successful = 0;
      let failed = 0;
      let duplicates = 0;

      for (let i = 0; i < attachments.length; i++) {
        const attachment = attachments[i];
        //console.log(`Processing attachment ${i + 1}/${attachments.length}: ${attachment.name}`);

        try {
          const result = await this.processSingleAttachment(attachment);
          results.push(result);

          if (result.success) {
            if (result.isDuplicate) {
              duplicates++;
            } else {
              successful++;
            }
          } else {
            failed++;
          }
        } catch (error) {
          console.error(`Error processing attachment ${attachment.name}:`, error);
          results.push({
            success: false,
            message: `Failed to process ${attachment.name}`,
            error: error instanceof Error ? error.message : "Unknown error",
          });
          failed++;
        }
      }

      //console.log(`✅ Processing complete: ${successful} uploaded, ${duplicates} duplicates, ${failed} failed`);

      return {
        total: attachments.length,
        successful,
        failed,
        duplicates,
        results,
      };
    } catch (error) {
      console.error("Error processing attachments:", error);
      throw error;
    }
  }

  // Helper methods

  private getEmailContext(): {
    itemId: string;
    subject: string;
    mode: "outlook" | "compose" | "read";
  } {
    const item = Office.context.mailbox.item;

    if (!item) {
      return {
        itemId: "unknown",
        subject: "Unknown Subject",
        mode: "outlook",
      };
    }

    // Determine mode
    let mode: "outlook" | "compose" | "read" = "outlook";
    if (item.to && typeof item.to.getAsync === "function") {
      mode = "compose";
    } else if (item.from || item.sender) {
      mode = "read";
    }

    return {
      itemId: item.itemId || "unknown",
      subject: item.subject || "No Subject",
      mode,
    };
  }

  private getContentType(
    fileName: string,
    format: Office.MailboxEnums.AttachmentContentFormat
  ): string {
    // First check the format from Office.js
    if (format === Office.MailboxEnums.AttachmentContentFormat.Base64) {
      // Try to determine content type from file extension
      const extension = fileName.split(".").pop()?.toLowerCase();

      const mimeTypes: { [key: string]: string } = {
        pdf: "application/pdf",
        doc: "application/msword",
        docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        xls: "application/vnd.ms-excel",
        xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        ppt: "application/vnd.ms-powerpoint",
        pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        jpg: "image/jpeg",
        jpeg: "image/jpeg",
        png: "image/png",
        gif: "image/gif",
        txt: "text/plain",
        zip: "application/zip",
        rar: "application/x-rar-compressed",
      };

      return mimeTypes[extension || ""] || "application/octet-stream";
    }

    return "application/octet-stream";
  }
}

// Step 10: Usage example and UI integration
class AttachmentUI {
  private handler: OutlookAttachmentHandler | null = null;

  async initialize(pamsBaseUrl: string, authToken: string) {
    this.handler = new OutlookAttachmentHandler(pamsBaseUrl, authToken);
  }

  async processAttachmentsWithUI() {
    if (!this.handler) {
      this.showNotification("Please connect to Pams first", "error");
      return;
    }

    try {
      // Show loading
      this.showNotification("Processing attachments...", "progress");

      // Process attachments
      const result = await this.handler.processAllAttachments();

      // Show results
      if (result.total === 0) {
        this.showNotification("No attachments found in this email", "info");
      } else {
        const message = this.buildResultMessage(result);
        this.showNotification(message, result.failed > 0 ? "warning" : "success");
      }
    } catch (error) {
      console.error("Error processing attachments:", error);
      this.showNotification(
        `Error: ${error instanceof Error ? error.message : "Unknown error"}`,
        "error"
      );
    }
  }

  private buildResultMessage(result: any): string {
    const parts = [];

    if (result.successful > 0) {
      parts.push(`${result.successful} uploaded`);
    }

    if (result.duplicates > 0) {
      parts.push(`${result.duplicates} duplicates found`);
    }

    if (result.failed > 0) {
      parts.push(`${result.failed} failed`);
    }

    return `Processed ${result.total} attachment(s): ${parts.join(", ")}`;
  }

  private showNotification(
    message: string,
    type: "success" | "error" | "info" | "warning" | "progress"
  ) {
    // Implementation depends on your UI framework
    //console.log(`[${type.toUpperCase()}] ${message}`);

    // For Outlook add-in notifications
    if (Office.context.mailbox.item && Office.context.mailbox.item.notificationMessages) {
      const notificationId = "attachmentProcessing";
      let messageType: Office.MailboxEnums.ItemNotificationMessageType;

      switch (type) {
        case "error":
          messageType = Office.MailboxEnums.ItemNotificationMessageType.ErrorMessage;
          break;
        case "progress":
          messageType = Office.MailboxEnums.ItemNotificationMessageType.ProgressIndicator;
          break;
        default:
          messageType = Office.MailboxEnums.ItemNotificationMessageType.InformationalMessage;
      }

      const notification: Office.NotificationMessageDetails = {
        type: messageType,
        message: message,
        icon: "Icon.16x16",
        persistent: type === "progress",
      };

      Office.context.mailbox.item.notificationMessages.replaceAsync(notificationId, notification);
    }
  }
}

// Export for use in your add-in
export { OutlookAttachmentHandler, AttachmentUI };

export async function getAllMailAttachments(): Promise<any[]> {
  //Office.AttachmentDetails
  return new Promise((resolve, reject) => {
    try {
      const item = Office.context.mailbox.item;

      if (!item) {
        reject(new Error("No email item available"));
        return;
      }

      // For read mode - attachments are directly available
      if (item.attachments && item.attachments.length > 0) {
        //console.log(`Found ${item.attachments.length} attachments in read mode`);
        resolve(item.attachments);
        return;
      }

      // For compose mode - use getAttachmentsAsync (requires Mailbox 1.8+)
      if (typeof item.getAttachmentsAsync === "function") {
        item.getAttachmentsAsync((result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            //console.log(`Found ${result.value.length} attachments in compose mode`);
            resolve(result.value);
          } else {
            reject(new Error(`Failed to get attachments: ${result.error?.message}`));
          }
        });
      } else {
        // Fallback: No attachments or method not available
        //console.log("No attachments found or getAttachmentsAsync not available");
        resolve([]);
      }
    } catch (error) {
      reject(error);
    }
  });
}
export async function fetchTagsListByType(listNum: number, includeArchived: boolean): Promise<any[]> {
  if(listNum){
    try {
      //gettagslistbytype
      const response = await fetch(serverUrl + `Common/Files/getoutlooktagslistbytype?CompanyBranchID=${getStoredAuth().user.CompanyBranchID}&listNum=${listNum}&includeArchived=${(includeArchived)? 1 : 0}`,
        {
          method: "GET",
          headers: requestHeaderWithAccessToken(),
        }
      );

      //console.log("fetchTagsListByType: ", await response);
      if (response.ok) return response.json();
    } catch (error) {
      console.error("Error:", error);
      return null;
    }
  }
}
export async function fetchFilePermissionByTypeAndRelatedToId(typeId: number, relatedId: number): Promise<any[]> {
  try {
    const response = await fetch(serverUrl + `person/person/getuserswithfilepermission?allowedBranches=${getStoredAuth().user.CompanyBranchID}&relatedToId=${relatedId}&relatedToTypeId=${typeId}`,
      {
        method: "GET",
        headers: requestHeaderWithAccessToken(),
      }
    );

    //console.log("fetchTagsListByType: ", await response);
    if (response.ok) return response.json();
  } catch (error) {
    console.error("Error:", error);
    return null;
  }
}
export async function fetchCategoriesListForAttachment(): Promise<any[]> {
  try {
    const response = await fetch(serverUrl + `common/filecategory/getlookup`,
      {
        method: "GET",
        headers: requestHeaderWithAccessToken(),
      }
    );

    //console.log("fetchTagsListByType: ", await response);
    if (response.ok) return response.json();
  } catch (error) {
    console.error("Error:", error);
    return null;
  }
}
export async function getOutlookAttachmentContent(attachmentId: string): Promise<{ content: string; format: any }> {
  //Office.MailboxEnums.AttachmentContentFormat
  return new Promise((resolve, reject) => {
    try {
      const item = Office.context.mailbox.item;

      if (!item || typeof item.getAttachmentContentAsync !== "function") {
        reject(new Error("getAttachmentContentAsync not available"));
        return;
      }
      item.getAttachmentContentAsync(attachmentId, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve({ content: result.value.content, format: result.value.format });
        } else {
          reject(new Error(`Failed to get attachment content: ${result.error?.message}`));
        }
      });
    } catch (error) {
      reject(error);
    }
  });
}
export async function uploadFilesToOneDrive(entity: PamsFileData[], acctualFiles: File[]): Promise<OneDriveFilesList[] | null>{
  try {
    const formData: FormData = new FormData();
    for (let i = 0; i < acctualFiles.length; i++) {
      formData.append(`uploads`, acctualFiles[i]);
      formData.append("contents", JSON.stringify(entity[i]));
    }
    const response = await fetch(APIOneDriveUrl + `file/uploadfiles`, {method: "POST", headers: requestHeaderForOneDrive, body: formData})
    //console.log("uploadFilesToOneDrive: ", await response);
    if (response.ok) return response.json();
  } catch (error) {
    console.error("Error:", error);
    return null;
  }
}
export async function uploadFilesData(entity: PamsFileData[]): Promise<PamsFileData[] | null>{
  try {
    const formData: FormData = new FormData();
    formData.append("content", JSON.stringify(entity));
    const response = await fetch(serverUrl + `Common/Files/uploadmsfiles`, {method: "POST", headers: requestHeaderWithAccessToken(), body: JSON.stringify(entity) })
    //console.log("uploadFilesData: ", await response);
    if (response.ok) return response.json();
  } catch (error) {
    console.error("Error:", error);
    return null;
  }
}